import { COOKIE_KEYS, PARAMS_COOKIE_KEYS } from 'Consts/cookies.enum'
import { useUserStore } from 'Stores/userStoreModuleFed'
import { DefaultAccount } from 'Types/LoginResponse'
import { UserAccount } from 'Types/UserAccount'
import { COOKIE_EXPIRATION_TIME, COOKIE_OPTIONS, CookieHelper } from 'Utils/cookieHelper'
import { hasKey } from 'Utils/object'
import LogRocket from 'logrocket'
import { LanguagesTypes } from 'Types/Languages'
import { useSetLanguage } from 'Hooks/useSetLanguage'
import { queryClient } from 'Lib/react-query'
import { useNavigate } from 'react-router-dom'
import { getAppcuesId } from 'Utils/appcues'

export type LoginParams = {
  token: string
  authCode: string
  userId: number
  language: LanguagesTypes
  defaultAccount: DefaultAccount
  subscriptionId: number | null
  email?: string
  multiAccount?: boolean
  userVerificationCodeRequired?: boolean
}

export const useLoginUser = (): {
  login: (a: Omit<LoginParams, 'authCode'>) => void
  setAuthCode: (authCode: string, rememberMe: boolean, userId: number) => void
  switchAccount: (account: UserAccount | undefined, redirectUrl?: string) => void
} => {
  const { setLanguage } = useSetLanguage()
  const { setCookie, getLogicCookieData } = CookieHelper()
  const { logIn, changeAccount, setIsLoggedIn } = useUserStore()
  const navigate = useNavigate()

  const login = async ({
    token,
    userId,
    defaultAccount,
    subscriptionId,
    email,
    language,
    multiAccount,
    userVerificationCodeRequired,
  }: Omit<LoginParams, 'authCode'>) => {
    LogRocket.identify(`${userId}`, {
      name: defaultAccount.accountName,
      email: email || '',
      environment: process.env.REACT_APP_ENVIRONMENT || '',
      subscriptionId: subscriptionId || '',
      accountId: defaultAccount.accountId,
    })

    logIn(
      token,
      userId,
      defaultAccount.accountId,
      defaultAccount.networkId,
      defaultAccount.owner,
      subscriptionId,
      defaultAccount.accountName,
      defaultAccount.isReactivation,
      multiAccount,
      userVerificationCodeRequired
    )
    setCookie(COOKIE_KEYS.TOKEN, token, COOKIE_OPTIONS)
    setCookie(COOKIE_KEYS.LANGUAGE, language, COOKIE_OPTIONS)
    setCookie(
      COOKIE_KEYS.PARAMS,
      JSON.stringify({
        [PARAMS_COOKIE_KEYS.USER_ID]: userId,
        [PARAMS_COOKIE_KEYS.SUBSCRIPTION_ID]: subscriptionId,
        [PARAMS_COOKIE_KEYS.IS_REACTIVATION]: defaultAccount.isReactivation,
        [PARAMS_COOKIE_KEYS.OWNER]: defaultAccount.owner.toLocaleString(),
        [PARAMS_COOKIE_KEYS.ACCOUNT_ID]: defaultAccount.accountId,
        [PARAMS_COOKIE_KEYS.ACCOUNT_NAME]: defaultAccount.accountName.toLocaleString(),
        [PARAMS_COOKIE_KEYS.NETWORK_ID]: defaultAccount.networkId,
      }),
      COOKIE_OPTIONS
    )
    await setLanguage(language)

    // init identification of user for appcues
    if (window.analytics) {
      window.analytics.identify(getAppcuesId(email))
    }
  }

  const setAuthCode = (authCode: string, rememberMe = false, userId: number) => {
    setIsLoggedIn(true)
    setCookie(COOKIE_KEYS.AUTH_CODE, JSON.stringify({ authCode, userId }), {
      expires: rememberMe ? COOKIE_EXPIRATION_TIME * 30 : COOKIE_EXPIRATION_TIME,
      secure: true,
    })
  }

  const switchAccount = (account: UserAccount | undefined, redirectUrl?: string) => {
    if (!account) return

    if (
      account.accountId &&
      hasKey(account, 'networkId') &&
      hasKey(account, 'accountId') &&
      hasKey(account, 'owner') &&
      hasKey(account, 'accountName') &&
      hasKey(account, 'subscriptionId')
    ) {
      //get all params
      const userId = getLogicCookieData(PARAMS_COOKIE_KEYS.USER_ID)
      const { subscriptionId, owner, accountId, networkId, accountName } = account
      // set new account
      changeAccount(accountId, networkId, owner, accountName, subscriptionId)
      setCookie(
        COOKIE_KEYS.PARAMS,
        JSON.stringify({
          [PARAMS_COOKIE_KEYS.USER_ID]: userId,
          [PARAMS_COOKIE_KEYS.SUBSCRIPTION_ID]: subscriptionId,
          [PARAMS_COOKIE_KEYS.OWNER]: owner.toLocaleString(),
          [PARAMS_COOKIE_KEYS.ACCOUNT_ID]: accountId,
          [PARAMS_COOKIE_KEYS.ACCOUNT_NAME]: account.accountName.toLocaleString(),
          [PARAMS_COOKIE_KEYS.NETWORK_ID]: networkId,
        }),
        COOKIE_OPTIONS
      )
      // refetrch all queries
      void queryClient.clear()
      if (!subscriptionId) {
        navigate('/pricing')
      } else {
        navigate(redirectUrl ? redirectUrl : '/')
      }
    }
  }

  return { login, setAuthCode, switchAccount }
}
